import { z } from 'zod';
import { AI_FEATURES, DOC_STATUSES, DOC_TYPES, MEDIA_OWNER_TYPES, MEDIA_SOURCES, SEVERITIES } from '../enums';
import { isoDate, uuid } from './common';
import { itemFilterSchema } from './work-items';

// ---------------------------------------------------------------------------------------------
// Search and saved filters (FR-SRCH-*)
// ---------------------------------------------------------------------------------------------

export const savedFilterSchema = z.object({
  name: z.string().min(1).max(80),
  projectId: uuid.nullable().optional(),
  query: itemFilterSchema.partial(),
  shared: z.boolean().default(false),
});
export type SavedFilterInput = z.infer<typeof savedFilterSchema>;

export const searchQuerySchema = z.object({
  q: z.string().min(1).max(200),
  types: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

// ---------------------------------------------------------------------------------------------
// Admin (FR-ADM-1)
// ---------------------------------------------------------------------------------------------

/** Triage ageing alerts (FR-ASG-12): first matching rule wins (type + optional severity). */
export const ageingRuleSchema = z.object({
  type: z.enum(['any', 'bug', 'story', 'task', 'subtask', 'quick_task']),
  severity: z.enum(SEVERITIES).optional(),
  hours: z.number().min(1).max(24 * 60),
});
export const orgTriageSettingsSchema = z.object({
  ageingAlerts: z.boolean().default(true),
  ageingRules: z.array(ageingRuleSchema).max(20).default([
    { type: 'bug', severity: 'critical', hours: 24 },
    { type: 'bug', hours: 72 },
    { type: 'story', hours: 72 },
    { type: 'any', hours: 120 },
  ]),
});
export type OrgTriageSettings = z.infer<typeof orgTriageSettingsSchema>;

export const orgSettingsSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  /** Record "viewed" in document activity (FR-DOC-13, optional; off by default for privacy). */
  documentViewTracking: z.boolean().optional(),
  emailNotifications: z.boolean().optional(),
  softDeleteRestoreDays: z.number().int().min(1).max(365).optional(),
  maxAttachmentMb: z.number().int().min(1).max(2048).optional(),
  maxDocumentMb: z.number().int().min(1).max(2048).optional(),
  maxMediaMb: z.number().int().min(1).max(10240).optional(),
  recordingLimitMin: z.number().int().min(1).max(240).optional(),
  timezone: z.string().max(64).optional(),
  /** DPDP Act: documented purpose and retention for employee time data (PRD §6). */
  dpdPurpose: z.string().max(4000).optional(),
  retention: z
    .object({
      worklogMonths: z.number().int().min(1).max(240),
      aiBodiesDays: z.number().int().min(1).max(3650),
      softDeleteDays: z.number().int().min(1).max(365),
      notificationDays: z.number().int().min(7).max(3650),
      messageDeletedDays: z.number().int().min(1).max(365),
    })
    .partial()
    .optional(),
  triage: orgTriageSettingsSchema.partial().optional(),
  media: z.object({ autoTranscribe: z.boolean(), coldAfterDays: z.number().int().min(7).max(3650), retentionDays: z.number().int().min(30).max(3650).nullable() }).partial().optional(),
  ai: z
    .object({
      enabled: z.boolean(),
      features: z.record(z.enum(AI_FEATURES), z.boolean()),
      dailyUserQuota: z.number().int().min(0),
      dailyOrgQuota: z.number().int().min(0),
    })
    .partial()
    .optional(),
});
export type OrgSettingsInput = z.infer<typeof orgSettingsSchema>;



export const csvImportSchema = z.object({
  projectKey: z.string(),
  csv: z.string().min(1).max(20_000_000),
  /** CSV column → Kanbix field. Unmapped columns are ignored. */
  mapping: z.record(z.string(), z.string()).optional(),
  dryRun: z.boolean().default(false),
});
export type CsvImportInput = z.infer<typeof csvImportSchema>;

// ---------------------------------------------------------------------------------------------
// Documents (FR-DOC-*)
// ---------------------------------------------------------------------------------------------

export const docFolderSchema = z.object({
  name: z.string().min(1).max(120),
  parentId: uuid.nullable().optional(),
  restricted: z.boolean().default(false),
  aclUserIds: z.array(uuid).default([]),
  aiAllowed: z.boolean().optional(),
});
export type DocFolderInput = z.infer<typeof docFolderSchema>;

export const documentUploadInitSchema = z.object({
  projectKey: z.string().nullable(),
  filename: z.string().min(1).max(255),
  mime: z.string().max(200),
  size: z.number().int().min(1),
  /** When set, the upload becomes a new version of this document. */
  documentId: uuid.optional(),
});
export type DocumentUploadInitInput = z.infer<typeof documentUploadInitSchema>;

export const documentUploadCompleteSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  docType: z.enum(DOC_TYPES).optional(),
  folderId: uuid.nullable().optional(),
  versionNotes: z.string().max(2000).optional(),
  tags: z.array(z.string().max(40)).optional(),
  description: z.string().max(4000).optional(),
  releaseId: uuid.nullable().optional(),
  consentConfirmed: z.boolean().optional(),
});
export type DocumentUploadCompleteInput = z.infer<typeof documentUploadCompleteSchema>;

export const updateDocumentSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  docType: z.enum(DOC_TYPES).optional(),
  /** Phase 2: status moves through the lifecycle endpoints (review → approve → publish → archive). */
  status: z.enum(DOC_STATUSES).optional(),
  folderId: uuid.nullable().optional(),
  product: z.string().max(120).nullable().optional(),
  releaseId: uuid.nullable().optional(),
  tags: z.array(z.string().max(40)).optional(),
  description: z.string().max(4000).nullable().optional(),
  restricted: z.boolean().optional(),
  aclUserIds: z.array(uuid).optional(),
  allowDownload: z.boolean().optional(),
  /** Whether document text may be sent to 360 AI; defaults to false when restricted. */
  aiAllowed: z.boolean().optional(),
});
export type UpdateDocumentInput = z.infer<typeof updateDocumentSchema>;

// Documents Phase 2 (FR-DOC-7/9/14/15, FR-AI-44/45/47) -------------------------------------------

export const submitDocReviewSchema = z.object({ reviewerIds: z.array(uuid).min(1).max(10), comment: z.string().max(2000).optional() });
export const docCommentSchema = z.object({
  body: z.unknown(),
  versionId: uuid.optional(),
  sectionId: z.string().max(64).optional(),
  page: z.number().int().min(1).optional(),
  parentId: uuid.optional(),
});
export const legalHoldSchema = z.object({ on: z.boolean(), reason: z.string().min(3).max(1000) });
export const docWaiverSchema = z.object({ docType: z.enum(DOC_TYPES), reason: z.string().min(3).max(1000) });
export const GUIDE_KINDS = ['user_guide', 'config_guide', 'release_notes'] as const;
export const guideDraftSchema = z.object({ releaseId: uuid, kind: z.enum(GUIDE_KINDS), baseDocumentId: uuid.optional(), audience: z.enum(['customer', 'internal']).default('customer') });
export const guideSaveSchema = z.object({ releaseId: uuid, title: z.string().min(1).max(200), markdown: z.string().min(1).max(200_000), kind: z.enum(GUIDE_KINDS) });

export const documentLinkSchema = z.object({
  targetType: z.enum(['release', 'build', 'work_item']),
  targetId: z.string().min(1),
});
export type DocumentLinkInput = z.infer<typeof documentLinkSchema>;

export const documentListQuery = z.object({
  type: z.enum(DOC_TYPES).optional(),
  release: uuid.optional(),
  folder: uuid.optional(),
  status: z.enum(DOC_STATUSES).optional(),
  q: z.string().max(200).optional(),
});

// ---------------------------------------------------------------------------------------------
// Media (FR-MED-*)
// ---------------------------------------------------------------------------------------------

export const mediaUploadInitSchema = z.object({
  ownerType: z.enum(MEDIA_OWNER_TYPES),
  ownerId: z.string().min(1),
  filename: z.string().min(1).max(255),
  mime: z.string().max(200),
  size: z.number().int().min(1),
  source: z.enum(MEDIA_SOURCES).default('upload'),
  consentConfirmed: z.boolean().optional(),
});
export type MediaUploadInitInput = z.infer<typeof mediaUploadInitSchema>;

export const attachmentInitSchema = z.object({
  ownerType: z.enum(['work_item', 'comment', 'note']),
  ownerId: z.string().min(1),
  filename: z.string().min(1).max(255),
  mime: z.string().max(200),
  size: z.number().int().min(1),
});
export type AttachmentInitInput = z.infer<typeof attachmentInitSchema>;

// ---------------------------------------------------------------------------------------------
// AI (ARCHITECTURE.md §19.8)
// ---------------------------------------------------------------------------------------------

export const aiDraftSchema = z.object({
  projectKey: z.string(),
  idea: z.string().min(3).max(8000),
  /** Optional base64 data URL screenshot for bug drafting (vision). */
  imageDataUrl: z.string().max(8_000_000).optional(),
});
export type AiDraftInput = z.infer<typeof aiDraftSchema>;

export const aiDuplicatesSchema = z.object({
  projectKey: z.string(),
  title: z.string().min(3).max(255),
  description: z.string().max(4000).optional(),
  excludeKey: z.string().optional(),
});

export const aiSearchSchema = z.object({ q: z.string().min(2).max(500), projectKey: z.string().optional() });

export const aiNoteActionsSchema = z.object({ noteId: uuid });

export const aiMessageSchema = z.object({
  text: z.string().min(1).max(8000),
  pageContext: z
    .object({
      route: z.string().optional(),
      projectKey: z.string().optional(),
      itemKey: z.string().optional(),
      sprintId: z.string().optional(),
      boardId: z.string().optional(),
      releaseId: z.string().optional(),
      buildId: z.string().optional(),
      noteId: z.string().optional(),
      documentId: z.string().optional(),
    })
    .partial()
    .optional(),
});
export type AiMessageInput = z.infer<typeof aiMessageSchema>;

export const aiFeedbackSchema = z.object({
  aiRequestId: uuid,
  rating: z.union([z.literal(1), z.literal(-1)]),
  comment: z.string().max(2000).optional(),
});

export const aiProposalConfirmSchema = z.object({
  /** User edits to the proposed arguments before confirming. */
  args: z.record(z.string(), z.unknown()).optional(),
});

export const requirementSelectionSchema = z.object({
  epics: z
    .array(
      z.object({
        ref: z.string(),
        title: z.string().min(1).max(255),
        description: z.string().max(10000).optional(),
        existingEpicKey: z.string().nullish(),
      }),
    )
    .default([]),
  stories: z
    .array(
      z.object({
        ref: z.string(),
        epicRef: z.string().nullish(),
        title: z.string().min(1).max(255),
        userStory: z.string().max(4000).optional(),
        acceptanceCriteria: z.array(z.string().max(2000)).default([]),
        sectionRefs: z.array(z.string()).default([]),
        storyPoints: z.number().min(0).max(100).nullish(),
        points: z.number().nullish(),
        type: z.string().default('story'),
      }),
    )
    .default([]),
  releaseId: uuid.nullable().optional(),
});
export type RequirementSelectionInput = z.infer<typeof requirementSelectionSchema>;

export const dateRangeQuery = z.object({ from: isoDate, to: isoDate });

// Media Phase 2 (FR-MED-8/9/12, FR-AI-50…55) ----------------------------------------------------------

export const mediaCommentSchema = z.object({ atMs: z.number().int().min(0), body: z.string().min(1).max(4000) });
export const mediaTrimSchema = z.object({ startMs: z.number().int().min(0), endMs: z.number().int().min(1) }).refine((v) => v.endMs > v.startMs, { message: 'End must be after start', path: ['endMs'] });
export const mediaActionItemsSchema = z.object({
  projectKey: z.string(),
  items: z
    .array(
      z.object({
        title: z.string().min(1).max(255),
        type: z.enum(['story', 'task', 'quick_task', 'bug']).default('task'),
        description: z.string().max(8000).optional(),
        assigneeId: uuid.nullable().optional(),
        dueDate: isoDate.nullable().optional(),
        atMs: z.number().int().min(0).nullable().optional(),
      }),
    )
    .min(1)
    .max(50),
});
export type MediaActionItemsInput = z.infer<typeof mediaActionItemsSchema>;
export const orgMediaSettingsSchema = z.object({
  autoTranscribe: z.boolean().default(true),
  /** Move originals to the infrequent-access tier after N days (HLS stays hot). */
  coldAfterDays: z.number().int().min(7).max(3650).default(90),
  /** Delete recordings after N days (null = keep); legal holds are respected. */
  retentionDays: z.number().int().min(30).max(3650).nullable().default(null),
});
export type OrgMediaSettings = z.infer<typeof orgMediaSettingsSchema>;

/** Retention defaults (FR-ADM-3); org.retention overrides any of them. */
export const RETENTION_DEFAULTS = { worklogMonths: 84, aiBodiesDays: 90, softDeleteDays: 30, notificationDays: 180, messageDeletedDays: 30 } as const;
export type RetentionPolicy = { -readonly [K in keyof typeof RETENTION_DEFAULTS]: number };

export const auditQuerySchema = z.object({
  before: z.string().datetime().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  action: z.string().max(100).optional(),
  actorId: z.string().uuid().optional(),
  targetType: z.string().max(50).optional(),
  targetId: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;
